import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiFileText, FiClock, FiCheckCircle, FiArrowRight, FiCalendar } from 'react-icons/fi';
import { exams as examsApi, results as resultsApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { formatDistanceToNow, format, isPast, isFuture } from 'date-fns';
import toast from 'react-hot-toast';

export function CandidateDashboard() {
  const { user } = useAuth();
  const [availableExams, setAvailableExams] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([examsApi.getAvailable(), resultsApi.getCandidate()])
      .then(([eRes, rRes]) => {
        setAvailableExams(eRes.data.data || []);
        setMyResults(rRes.data.data || []);
      })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const completedExams = availableExams.filter(e => ['submitted','auto_submitted','timed_out'].includes(e.attempt_status));
  const pendingExams = availableExams.filter(e => !['submitted','auto_submitted','timed_out'].includes(e.attempt_status));
  const avgScore = myResults.length ? (myResults.reduce((a, r) => a + parseFloat(r.percentage), 0) / myResults.length).toFixed(1) : null;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-black text-gray-900">Welcome back, {user?.first_name}! 👋</h1>
        <p className="text-gray-500 text-sm">Here's your assessment overview</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Available Exams', value: pendingExams.length, icon: FiFileText, color: 'bg-navy' },
          { label: 'Completed', value: completedExams.length, icon: FiCheckCircle, color: 'bg-emerald-500' },
          { label: 'Avg Score', value: avgScore ? `${avgScore}%` : '—', icon: FiArrowRight, color: 'bg-amber-500' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="stat-card">
            <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}><Icon size={18} className="text-white" /></div>
            <div><p className="text-xs text-gray-500">{label}</p><p className="text-xl font-black text-gray-900">{value}</p></div>
          </div>
        ))}
      </div>

      {pendingExams.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Upcoming Exams</h3>
            <Link to="/candidate/exams" className="text-sm text-navy font-semibold hover:underline">View all</Link>
          </div>
          <div className="space-y-3">
            {pendingExams.slice(0, 3).map(exam => (
              <div key={exam.id} className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-navy/30 transition-colors">
                <div className="w-10 h-10 bg-navy/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FiFileText size={18} className="text-navy" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{exam.title}</p>
                  <p className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                    <FiClock size={11} /> {exam.duration_minutes} min
                    {exam.start_time && <><FiCalendar size={11} /> {format(new Date(exam.start_time), 'MMM d, HH:mm')}</>}
                  </p>
                </div>
                <Link to={`/exam/${exam.id}`}
                  className={`btn-primary text-sm py-2 ${exam.start_time && isFuture(new Date(exam.start_time)) ? 'opacity-50 pointer-events-none' : ''}`}>
                  {exam.attempt_status === 'in_progress' ? 'Resume' : 'Start'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {myResults.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Recent Results</h3>
            <Link to="/candidate/results" className="text-sm text-navy font-semibold hover:underline">View all</Link>
          </div>
          <div className="space-y-2">
            {myResults.slice(0, 3).map(r => (
              <Link key={r.id} to={`/candidate/results/${r.attempt_id}`}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${r.is_passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {parseFloat(r.percentage).toFixed(0)}%
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{r.exam_title}</p>
                  <p className="text-xs text-gray-400">{formatDistanceToNow(new Date(r.evaluated_at), { addSuffix: true })}</p>
                </div>
                <span className={r.is_passed ? 'badge-pass' : 'badge-fail'}>{r.is_passed ? 'PASS' : 'FAIL'}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {!loading && availableExams.length === 0 && (
        <div className="card p-12 text-center">
          <FiFileText size={40} className="text-gray-200 mx-auto mb-3" />
          <h3 className="font-bold text-gray-700 mb-1">No exams available</h3>
          <p className="text-gray-400 text-sm">You'll see your assigned exams here once the admin registers you.</p>
        </div>
      )}
    </div>
  );
}

export function ExamListCandidatePage() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    examsApi.getAvailable().then(r => setExams(r.data.data || [])).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, []);

  const getExamStatus = (exam) => {
    if (['submitted','auto_submitted','timed_out'].includes(exam.attempt_status)) return { label: 'Completed', color: 'text-emerald-700 bg-emerald-50' };
    if (exam.attempt_status === 'in_progress') return { label: 'In Progress', color: 'text-blue-700 bg-blue-50' };
    if (exam.start_time && isFuture(new Date(exam.start_time))) return { label: 'Upcoming', color: 'text-amber-700 bg-amber-50' };
    if (exam.end_time && isPast(new Date(exam.end_time))) return { label: 'Expired', color: 'text-gray-600 bg-gray-100' };
    return { label: 'Available', color: 'text-navy bg-navy/10' };
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <h1 className="text-2xl font-black text-gray-900">My Exams</h1>
      {loading ? (
        <div className="grid gap-4">{[...Array(3)].map((_, i) => <div key={i} className="h-28 card animate-pulse" />)}</div>
      ) : exams.length === 0 ? (
        <div className="card p-12 text-center"><FiFileText size={40} className="text-gray-200 mx-auto mb-3" /><p className="text-gray-400">No exams assigned yet</p></div>
      ) : (
        <div className="grid gap-4">
          {exams.map(exam => {
            const status = getExamStatus(exam);
            const isCompleted = ['submitted','auto_submitted','timed_out'].includes(exam.attempt_status);
            const isUpcoming = exam.start_time && isFuture(new Date(exam.start_time));
            const isExpired = exam.end_time && isPast(new Date(exam.end_time));
            return (
              <div key={exam.id} className="card p-5 hover:shadow-card-hover transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-navy/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FiFileText size={22} className="text-navy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h3 className="font-bold text-gray-900">{exam.title}</h3>
                        {exam.description && <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{exam.description}</p>}
                      </div>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${status.color}`}>{status.label}</span>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><FiClock size={11} /> {exam.duration_minutes} min</span>
                      <span className="flex items-center gap-1"><FiFileText size={11} /> {exam.total_questions} questions</span>
                      {exam.start_time && <span className="flex items-center gap-1"><FiCalendar size={11} /> {format(new Date(exam.start_time), 'MMM d, yyyy HH:mm')}</span>}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {isCompleted ? (
                      <Link to={`/candidate/results/${exam.attempt_id}`} className="btn-secondary text-sm py-2">View Result</Link>
                    ) : isExpired ? (
                      <span className="text-sm text-gray-400 font-medium">Expired</span>
                    ) : isUpcoming ? (
                      <span className="text-sm text-amber-600 font-medium">{formatDistanceToNow(new Date(exam.start_time))}</span>
                    ) : (
                      <Link to={`/exam/${exam.id}`} className="btn-primary text-sm py-2">
                        {exam.attempt_status === 'in_progress' ? '▶ Resume' : '▶ Start Exam'}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CandidateDashboard;
